import { Injectable, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { AuthService } from './auth.service';
import { environment as env } from '../../environments/environment';
import { ToastService } from './toast.service';
const localLog = console.log;

@Injectable({
  providedIn: 'root'
})
export class BillingService {

  constructor(private http: HttpClient,
    private loadingService: LoadingService,
    private authService: AuthService,
    private toast: ToastService) {
  }

  private _get_headers() {
    return {      withCredentials: true
    };
  }

  async getBillingUsers(query: any): Promise<any> {
    const url = `${env.apiUrl}/rest/billing/users`;
    let _id = this.loadingService.add('Loading Users');
    try {
      let res: any = await this.http.post(url, query, this._get_headers()).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getBillingUsers() =>', e);
      this._reportBillingFailure('F2ADMIN_BILLING_USERS_E01', 'Failed to get billing users', url, e, { query });
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  // IT-F2-157 2026-10-30 — billable_only narrows T/C-source counts to
  // users whose UserTracking grant has a matching non-fallback
  // CommerceEvents row (see f2-admin-service billing.service.ts ::
  // _build_billable_sets). Pre-TTL months come back as null and get
  // rendered as "--" by the SPA.
  // Mike c/99935e0b (2026-10-30) — customer_filter short-circuits the
  // fleet-wide fan-out when the SPA is scoped to a single customer.
  async getBillingReport(full_report: boolean, mta_live_data: boolean, billable_only: boolean = false, customer_filter?: string): Promise<any> {
    let url = `${env.apiUrl}/rest/billing/report?full_report=${full_report}&mta_live_data=${mta_live_data}&billable_only=${billable_only}`;
    if (customer_filter) url += `&customer=${encodeURIComponent(customer_filter)}`;
    let _id = this.loadingService.add('Loading billing report...');
    try {
      let res: any = await this.http.get(url, this._get_headers()).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getBillingReport() =>', e);
      this._reportBillingFailure('F2ADMIN_BILLING_REPORT_E01', 'Failed to get billing report', url, e, { full_report, mta_live_data, billable_only });
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async getTheoTradeBilling(refresh: boolean, include_last_login: boolean): Promise<any> {
    const url = `${env.apiUrl}/rest/billing/theotrade-report?refresh=${refresh}&include_last_login=${include_last_login}`;
    let _id = this.loadingService.add('Loading billing report...');
    try {
      let res: any = await this.http.get(url, this._get_headers()).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getTheoTradeBilling() =>', e);
      this._reportBillingFailure('F2ADMIN_BILLING_THEOTRADE_E01', 'Failed to get TheoTrade billing report', url, e, { refresh, include_last_login });
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async getTheoTradeBillingExternal(include_last_login: boolean): Promise<any> {
    const url = `${env.apiUrl}/rest/billing/theotrade-report-jr?include_last_login=${include_last_login}`;
    let _id = this.loadingService.add('Loading billing report...');
    try {
      let res: any = await this.http.get(url, this._get_headers()).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getTheoTradeBillingExternal() =>', e);
      this._reportBillingFailure('F2ADMIN_BILLING_THEOTRADE_JR_E01', 'Failed to get TheoTrade billing report', url, e, { include_last_login });
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  async getUserTracking(query: any): Promise<any> {
    const url = `${env.apiUrl}/rest/billing/user-tracking`;
    let _id = this.loadingService.add('Loading Users');
    try {
      let res: any = await this.http.post(url, query, this._get_headers()).toPromise();
      if (res?.err) throw new Error(res.err);
      this.loadingService.remove(_id);
      return res;
    }
    catch (e: any) {
      console.log('Error getUserTracking() =>', e);
      this._reportBillingFailure('F2ADMIN_BILLING_USER_TRACKING_E01', 'Failed to get user tracking', url, e, { query });
      this.loadingService.remove(_id);
      return { err: e.message }
    }
  }

  /**
   * Wrap the toast error with a full diagnostic bundle so the failure
   * lands in /admin/error-reports with a support ID. Previously the
   * calls were `toast.error(title, message)` with no `error_code` — the
   * ToastService's ER-submit code path silently drops when required
   * fields are missing so Mike had to eyeball the toast text alone
   * (2026-07-06 billing-reports 'Http failure during parsing' bug).
   *
   * Diagnostics captured: HTTP status/URL, response body preview,
   * exception name/message, whichever inputs the caller was using.
   * Mirror of the pattern in error-reports.component's own SPA-side
   * capture (feedback_silent_bounce_needs_jwt_peek). Backend caps
   * strings at write time so operator-supplied query values are safe.
   */
  private _reportBillingFailure(
    error_code: string,
    title: string,
    url: string,
    err: any,
    extras: Record<string, any>,
  ): void {
    const status = err?.status ?? err?.error?.status;
    const responseText = typeof err?.error === 'string'
      ? err.error.slice(0, 300)
      : (err?.error?.text || err?.error?.message || err?.message || '');
    const diagnostics: any = {
      error_code,
      customer: 'f2-admin',
      scanner_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      url,
      http_status: typeof status === 'number' ? status : undefined,
      f2_response_body: String(responseText).slice(0, 500),
      f2_response_message: err?.message || String(err),
      js_error_name: err?.name || undefined,
      js_error_stack: typeof err?.stack === 'string' ? err.stack.slice(0, 800) : undefined,
      ...extras,
    };
    this.toast.error(title, err?.message || String(err), diagnostics);
  }

  generateCSV(data: any[]) {
    if (!data || data.length === 0) return '';

    const escape = (val: any) => {
      if (val === null || val === undefined) return '';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const header = Object.keys(data[0]).map(escape).join(',') + '\n';
    const rows = data
      .map(row => Object.values(row).map(escape).join(','))
      .join('\n');

    return header + rows;
  }
}
